import { scrollToComment } from "@/utils/scrollToComment";
import {
  convertMentionsToUserIds,
  createMentionMap,
  extractMentionsFromText,
} from "@roman/shared/features/comment/mentionParser";
import { usePostComment } from "@roman/shared/hooks/PredictionMarket/usePostComment";
import { useAuthStore } from "@roman/shared/store/auth.store";
import type { ChildPredictionMarketComment, ParentPredictionMarketComment } from "@roman/shared/types/Comment";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CommentInput } from "./CommentInput";

type NewChildCommentProps = {
  comment: ParentPredictionMarketComment;
  onReplySuccess?: (comment: ChildPredictionMarketComment) => void;
  onCancel: () => void;
};

export const NewChildComment = ({ comment, onReplySuccess, onCancel }: NewChildCommentProps) => {
  const user = useAuthStore((state) => state.user);
  const [replyText, setReplyText] = useState(`@${comment.author.name} `);
  const [shouldClearInput, setShouldClearInput] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  // Create mention map for converting @mentions to user IDs
  const mentionMap = useMemo(() => {
    return createMentionMap([
      {
        mention: `@${comment.author.name}`,
        authorId: comment.authorId,
        authorName: comment.author.name,
      },
    ]);
  }, [comment.author.name, comment.authorId]);

  const { postComment, isMutating, error } = usePostComment(
    {
      onSuccess: (comment) => {
        onReplySuccess?.(comment as unknown as ChildPredictionMarketComment);
        setShouldClearInput(true);
        setTimeout(() => setShouldClearInput(false), 100);
        toast.success("コメントを投稿しました");
      },
    },
    (newComment) => {
      scrollToComment(newComment.id);
    },
  );

  const handleSubmitReply = () => {
    if (replyText.trim() && !isMutating) {
      // Extract mentioned user IDs from the text using the mention map
      const mentionedUserIds = extractMentionsFromText(replyText.trim(), mentionMap);

      // Convert @mentions to <@userId> format in the text (Slack-style)
      const processedText = convertMentionsToUserIds(replyText.trim(), mentionMap);

      postComment({
        type: "child" as const,
        predictionMarketId: comment.predictionMarketId,
        parentId: comment.id,
        text: processedText, // Send text with <@userId> format
        mentionedUserIds, // Dynamic mention extraction
      });
      setReplyText("");
      handleCancel();
    }
  };

  const handleCancel = () => {
    setTimeout(() => {
      onCancel?.();
    }, 100);
  };

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  // Reset input when comment changes
  useEffect(() => {
    setReplyText(`@${comment.author.name} `);
  }, [comment.author.name]);

  // Scroll to make this component visible when it mounts
  useEffect(() => {
    const scrollToComponent = () => {
      if (componentRef.current) {
        componentRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center", // Center the element in the viewport
          inline: "nearest",
        });
      }
    };

    // Small delay to ensure component is fully rendered
    const timeoutId = setTimeout(scrollToComponent, 100);

    return () => clearTimeout(timeoutId);
  }, []); // Empty dependency array means this runs only on mount

  return (
    <>
      <div ref={componentRef} className="flex gap-[10px] mt-[15px]">
        {/* User avatar */}
        <div className="relative">
          <img
            src={user?.photoUrl || "/assets/player-shirt.png"}
            alt="My avatar"
            className="w-[36px] h-[36px] rounded-full object-cover"
          />
        </div>

        {/* Comment input */}
        <div className="flex-1">
          <CommentInput
            setSearch={setReplyText}
            onSubmit={handleSubmitReply}
            onCancel={handleCancel}
            isSubmitting={isMutating}
            clearInput={shouldClearInput}
            autoFocus={true}
            isReply={true}
            initialValue={`@${comment.author.name} `}
            mention={comment.author.name}
          />
        </div>
      </div>
    </>
  );
};
